import { format } from 'date-fns'
import helper from './helper'
import axios from '@/axios'

/**
 * CRITICAL: Format a Date object as a LOCAL ISO string without timezone indicator.
 *
 * JavaScript's Date.toISOString() converts to UTC and adds 'Z' suffix.
 * This causes timezone shift when the backend parses it.
 *
 * Instead, we format as "YYYY-MM-DDTHH:mm:ss.SSS" (no Z) which the backend
 * will interpret as the intended local time without conversion.
 *
 * Example:
 *   Input: Date object for "Jan 26 00:00 SAST"
 *   toISOString(): "2026-01-25T22:00:00.000Z" (WRONG - converts to UTC)
 *   formatLocalDateTime(): "2026-01-26T00:00:00.000" (CORRECT - preserves local time)
 */
function formatLocalDateTime(date) {
  if (!date) return date
  if (typeof date === 'string') return date // Already a string
  if (!(date instanceof Date)) return date // Not a date

  return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS")
}

/**
 * Convert all Date fields in a schedule item to local ISO strings
 * to prevent timezone shift when sending to backend
 */
function convertDatesToLocalStrings(item) {
  const converted = { ...item }

  // Convert main start/end times
  if (converted.start_time) {
    converted.start_time = formatLocalDateTime(converted.start_time)
  }
  if (converted.end_time) {
    converted.end_time = formatLocalDateTime(converted.end_time)
  }

  // Convert additional slots if present
  if (converted.additional && Array.isArray(converted.additional)) {
    converted.additional = converted.additional.map(slot => ({
      ...slot,
      start_time: formatLocalDateTime(slot.start_time),
      end_time: formatLocalDateTime(slot.end_time)
    }))
  }

  return converted
}

export default {
  async getByChannel(id, start_time, end_time) {
    return await helper.post(`/api/schedules/by-channel`, {
      channel_id: id,
      start_time: formatLocalDateTime(start_time),
      end_time: formatLocalDateTime(end_time)
    })
  },
  async add(item) {
    // CRITICAL: Convert dates to local strings to prevent timezone shift
    return await helper.post(`/api/schedules`, convertDatesToLocalStrings(item))
  },
  async update(item) {
    // CRITICAL: Convert dates to local strings to prevent timezone shift
    return await helper.put(`/api/schedules`, convertDatesToLocalStrings(item))
  },
  async remove(item) {
    return await helper.delete(`/api/schedules/${item.id}`)
  },
  async removeMultiple(ids) {
    return await helper.post(`/api/schedules/remove-multiple`, { ids: ids })
  },
  async exportEpg(id, start_time, end_time, channelName) {
    // CRITICAL: Format dates as local datetime strings to prevent timezone shift
    const startTimeStr = formatLocalDateTime(start_time)
    const endTimeStr = formatLocalDateTime(end_time)

    const response = await axios({
      url: `/api/schedules/export-epg?channel_id=${id}&start_time=${encodeURIComponent(startTimeStr)}&end_time=${encodeURIComponent(endTimeStr)}`,
      method: 'GET',
      responseType: 'blob'
    })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute(
      'download',
      `epg-${encodeURIComponent(channelName)}-${format(
        start_time,
        'ddMMyy'
      )}-${format(end_time, 'ddMMyy')}.xml`
    )
    document.body.appendChild(fileLink)

    fileLink.click()
  },
  async exportNielsen(id, start_time, end_time, channelName) {
    // CRITICAL: Format dates as local datetime strings to prevent timezone shift
    const startTimeStr = formatLocalDateTime(start_time)
    const endTimeStr = formatLocalDateTime(end_time)

    const response = await axios({
      url: `/api/schedules/export-nielsen?channel_id=${id}&start_time=${encodeURIComponent(startTimeStr)}&end_time=${encodeURIComponent(endTimeStr)}`,
      method: 'GET',
      responseType: 'blob'
    })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute(
      'download',
      `nielsen-${encodeURIComponent(channelName)}-${format(
        start_time,
        'ddMMyy'
      )}-${format(end_time, 'ddMMyy')}.xlsx`
    )
    document.body.appendChild(fileLink)

    fileLink.click()
  },
  async exportBreak(id, start_time, end_time, channelName) {
    // CRITICAL: Format dates as local datetime strings to prevent timezone shift
    const startTimeStr = formatLocalDateTime(start_time)
    const endTimeStr = formatLocalDateTime(end_time)

    const response = await axios({
      url: `/api/schedules/export-break?channel_id=${id}&start_time=${encodeURIComponent(startTimeStr)}&end_time=${encodeURIComponent(endTimeStr)}`,
      method: 'GET',
      responseType: 'blob'
    })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute(
      'download',
      `break-${encodeURIComponent(channelName)}-${format(
        start_time,
        'ddMMyy'
      )}-${format(end_time, 'ddMMyy')}.csv`
    )
    document.body.appendChild(fileLink)

    fileLink.click()
  },
  async exportPlayout(id, date, channelName, options = {}) {
    const { useGatewayPaths = false, pathSource = null } = options
    const gatewayParam = useGatewayPaths ? '&useGatewayPaths=true' : ''
    const sourceParam = pathSource ? `&pathSource=${pathSource}` : ''

    const response = await axios({
      url: `/api/schedules/export-playout?channel_id=${id}&date=${format(date, 'yyyy-MM-dd')}${gatewayParam}${sourceParam}`,
      method: 'GET',
      responseType: 'blob'
    })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute(
      'download',
      `MCRList-${encodeURIComponent(channelName)}-${format(date, 'ddMMyy')}.MCRLIST`
    )
    document.body.appendChild(fileLink)

    fileLink.click()
  },
  /**
   * Import a BOSS-exported playlist and get back a Cinegy MCRList plus an exact
   * conflict report. Returns { stats, conflicts, warnings, airReady, xml }.
   *
   * Two-file merge (preferred): csvContent resolves names (more shows), xmlContent
   * supplies the ENHANCEDSCTE opt-out positions (index-correlated -> type="C").
   * CSV-only is valid (no type="C"). Does NOT download — caller handles the result.
   */
  async importBossPlaylist(channelId, { csvContent = null, xmlContent = null, useGatewayPaths = false, pathSource = null } = {}) {
    // pathSource = 'channel' | 'secondary' | 'gateway' (Phase 1 of multi-target playout).
    // useGatewayPaths kept for backward-compat — when true backend resolves
    // a VersityGW-style smb_share_path from storage_config.
    const response = await axios({
      url: '/api/import/boss-playlist',
      method: 'POST',
      data: { channelId, csvContent, xmlContent, useGatewayPaths, pathSource }
    })
    return response.data
  },
  /**
   * Export playout schedule for a date range (MCR List format)
   */
  async exportPlayoutRange(id, startDate, endDate, channelName, options = {}) {
    const { useGatewayPaths = false, pathSource = null } = options
    const startDateStr = format(startDate, 'yyyy-MM-dd')
    const endDateStr = format(endDate, 'yyyy-MM-dd')
    const gatewayParam = useGatewayPaths ? '&useGatewayPaths=true' : ''
    const sourceParam = pathSource ? `&pathSource=${pathSource}` : ''

    const response = await axios({
      url: `/api/schedules/export-playout?channel_id=${id}&start_date=${startDateStr}&end_date=${endDateStr}${gatewayParam}${sourceParam}`,
      method: 'GET',
      responseType: 'blob'
    })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    const dateRangeStr = startDateStr === endDateStr
      ? format(startDate, 'ddMMyy')
      : `${format(startDate, 'ddMMyy')}-${format(endDate, 'ddMMyy')}`
    fileLink.setAttribute(
      'download',
      `MCRList-${encodeURIComponent(channelName)}-${dateRangeStr}.MCRLIST`
    )
    document.body.appendChild(fileLink)

    fileLink.click()
  },
  /**
   * Export to BXF (Broadcast Exchange Format) - SMPTE ST 2021
   * Universal format for importing into various playout/automation systems
   */
  async exportBXF(channelId, date, channelName, options = {}) {
    const { useGatewayPaths = false, pathSource = null } = options
    const params = []
    if (useGatewayPaths) params.push('useGatewayPaths=true')
    if (pathSource) params.push(`pathSource=${pathSource}`)
    const qs = params.length ? `?${params.join('&')}` : ''

    const response = await axios({
      url: `/api/export/bxf/${channelId}/${format(date, 'yyyy-MM-dd')}${qs}`,
      method: 'GET',
      responseType: 'blob'
    })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute(
      'download',
      `BXF-${encodeURIComponent(channelName)}-${format(date, 'yyyy-MM-dd')}.xml`
    )
    document.body.appendChild(fileLink)

    fileLink.click()
  },
  /**
   * Export to BXF for a date range
   */
  async exportBXFRange(channelId, startDate, endDate, channelName, options = {}) {
    const { useGatewayPaths = false, pathSource = null } = options
    const startDateStr = format(startDate, 'yyyy-MM-dd')
    const endDateStr = format(endDate, 'yyyy-MM-dd')
    const gatewayParam = useGatewayPaths ? '&useGatewayPaths=true' : ''
    const sourceParam = pathSource ? `&pathSource=${pathSource}` : ''

    const response = await axios({
      url: `/api/export/bxf/${channelId}/${startDateStr}?end_date=${endDateStr}${gatewayParam}${sourceParam}`,
      method: 'GET',
      responseType: 'blob'
    })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    const dateRangeStr = startDateStr === endDateStr
      ? startDateStr
      : `${startDateStr}_to_${endDateStr}`
    fileLink.setAttribute(
      'download',
      `BXF-${encodeURIComponent(channelName)}-${dateRangeStr}.xml`
    )
    document.body.appendChild(fileLink)

    fileLink.click()
  },
  async exportProduction(id, start_time, end_time, channelName) {
    // CRITICAL: Format dates as local datetime strings to prevent timezone shift
    const startTimeStr = formatLocalDateTime(start_time)
    const endTimeStr = formatLocalDateTime(end_time)

    const response = await axios({
      url: `/api/schedules/export-production?channel_id=${id}&start_time=${encodeURIComponent(startTimeStr)}&end_time=${encodeURIComponent(endTimeStr)}`,
      method: 'GET',
      responseType: 'blob'
    })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute(
      'download',
      `production-${encodeURIComponent(channelName)}-${format(
        start_time,
        'ddMMyy'
      )}-${format(end_time, 'ddMMyy')}.xlsx`
    )
    document.body.appendChild(fileLink)

    fileLink.click()
  },
  async exportCatchup(id, start_time, end_time, channelName) {
    // CRITICAL: Format dates as local datetime strings to prevent timezone shift
    const startTimeStr = formatLocalDateTime(start_time)
    const endTimeStr = formatLocalDateTime(end_time)

    const response = await axios({
      url: `/api/schedules/export-catchup?channel_id=${id}&start_time=${encodeURIComponent(startTimeStr)}&end_time=${encodeURIComponent(endTimeStr)}`,
      method: 'GET',
      responseType: 'blob'
    })
    const fileURL = window.URL.createObjectURL(new Blob([response.data]))
    const fileLink = document.createElement('a')

    fileLink.href = fileURL
    fileLink.setAttribute(
      'download',
      `catchup-${encodeURIComponent(channelName)}-${format(
        start_time,
        'ddMMyy'
      )}-${format(end_time, 'ddMMyy')}.xlsx`
    )
    document.body.appendChild(fileLink)

    fileLink.click()
  },
  async fetchSequence(id) {
    return await helper.get(`/api/schedule-repeats/${id}`)
  },
  async fetchSequenceItems(id) {
    return await helper.get(`/api/schedule-repeats/items/${id}`)
  },
  async deleteSequence(id) {
    return await helper.delete(`/api/schedule-repeats/${id}`)
  },
  async updateSequenceTitle(item) {
    // Convert dates to local strings to prevent timezone shift
    return await helper.put(`/api/schedules/sequence-title`, convertDatesToLocalStrings(item))
  },
  async copySequence(item) {
    // Convert dates to local strings to prevent timezone shift
    return await helper.post(`api/schedules/sequence-copy`, convertDatesToLocalStrings(item))
  },
  async copyWeek(item) {
    // Convert dates to local strings to prevent timezone shift
    return await helper.post(`api/schedules/copy-week`, convertDatesToLocalStrings(item))
  },
  async getDayView(channelId, date) {
    return await helper.get(`/api/dayview/${channelId}/${date}`)
  },
  async getScheduleChanges(channelId, date) {
    return await helper.get(`/api/dayview/${channelId}/${date}/changes`)
  },
  async insertMedia(payload) {
    // Convert dates to local strings to prevent timezone shift
    return await helper.post(`/api/schedules/insert-media`, convertDatesToLocalStrings(payload))
  },
  async moveScheduleItem(scheduleId, payload) {
    // Convert dates to local strings to prevent timezone shift
    return await helper.put(`/api/schedules/${scheduleId}/move`, convertDatesToLocalStrings(payload))
  },
  async bulkReplace(payload) {
    // Convert dates to local strings to prevent timezone shift
    return await helper.post(`/api/schedules/bulk-replace`, convertDatesToLocalStrings(payload))
  },
  async copyDayShortform(payload) {
    // Convert dates to local strings to prevent timezone shift
    return await helper.post(`/api/schedules/copy-day-shortform`, convertDatesToLocalStrings(payload))
  }
}
